/**
 * Level 2 detriment panel — three columns, one per detriment type in severity
 * order. The panel describes what the (scoped) period is made of, so it is the
 * same regardless of the Plot toggle.
 *
 * ONE denominator for every percentage: records in the scoped period (SPEC
 * §6.2.3). Type lead % = type-count / denom; each sub-theme % = label-count /
 * denom. Whole-period counts come from summing the series; scoped counts from
 * the selected bucket / label.by_bucket.
 */

import { useMemo } from 'react';

import { DETRIMENT_META } from '../constants';
import type { DetailData } from '../types';
import { recordsLabel, sumDetriment } from '../utils';

interface ColLabel {
  name: string;
  count: number;
}

export function DetrimentColumns({
  detail,
  selectedBucket,
}: {
  detail: DetailData;
  selectedBucket: string | null;
}) {
  const { columns, denom } = useMemo(() => {
    const point = selectedBucket
      ? detail.series.find((p) => p.bucketStart === selectedBucket)
      : null;
    const denom = Math.round(selectedBucket ? point?.volume ?? 0 : detail.outcome.volume) || 1;
    const totals = selectedBucket
      ? point?.detriment ?? { dissatisfaction: 0, strain: 0, harm: 0 }
      : sumDetriment(detail.series);

    const columns = DETRIMENT_META.map((meta) => {
      const total = Math.round(totals[meta.type]);
      const labels: ColLabel[] = detail.detrimentLabels
        .filter((l) => l.type === meta.type)
        .map((l) => ({
          name: l.name,
          count: selectedBucket ? l.byBucket[selectedBucket] ?? 0 : l.volume,
        }))
        .filter((l) => l.count > 0)
        .sort((a, b) => b.count - a.count);
      return { meta, total, lead: labels[0], supporting: labels.slice(1, 5) };
    });
    return { columns, denom };
  }, [detail, selectedBucket]);

  return (
    <div>
      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--soft,#ECECEE)] pb-2 text-[9px] font-extrabold uppercase tracking-[0.08em] text-muted-foreground">
        <span>Detriment mix</span>
        <span className="normal-case tracking-normal">
          Top sub-themes within each dimension · share of the {recordsLabel(denom)} in this period
        </span>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        {columns.map(({ meta, total, lead, supporting }) => (
          <div key={meta.type} className="min-w-0">
            <div className="mb-3 flex items-baseline justify-between gap-2 border-b border-border pb-2">
              <span className="text-[15px] font-extrabold tracking-[-0.01em]" style={{ color: meta.color }}>
                {meta.label}
              </span>
              <span className="text-[9px] font-extrabold uppercase tracking-[0.1em] text-muted-foreground">
                {meta.tag}
              </span>
            </div>

            <div
              className="flex flex-wrap items-start justify-between gap-3 rounded-[10px] p-4 text-white"
              style={{ background: meta.color }}
            >
              <div className="text-[1.875rem] font-extrabold leading-none tracking-[-0.035em]">
                {((total / denom) * 100).toFixed(1)}%
              </div>
              <div className="min-w-0 text-right">
                <div className="text-xs font-bold leading-tight">{lead?.name ?? '—'}</div>
                <div className="mt-0.5 text-[11px] font-medium opacity-80">
                  {recordsLabel(total)} in total
                </div>
              </div>
            </div>

            {supporting.map((r) => (
              <div
                key={r.name}
                className="mt-1.5 flex items-center justify-between gap-3 rounded-lg px-3 py-2.5"
                style={{ background: `${meta.color}14` }}
              >
                <div className="min-w-0">
                  <div className="text-xs font-semibold leading-tight">{r.name}</div>
                  <div className="mt-px text-[10px] font-medium text-muted-foreground">
                    {recordsLabel(r.count)}
                  </div>
                </div>
                <div className="shrink-0 text-sm font-extrabold tabular-nums" style={{ color: meta.color }}>
                  {((r.count / denom) * 100).toFixed(1)}%
                </div>
              </div>
            ))}

            {total === 0 && (
              <div className="mt-2 text-[11px] font-medium text-muted-foreground">
                None in this period
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
